import { alpha, Avatar, Stack } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  _getInvestorOwnedEntities,
  INTRODUCER_DASHBOARD_QUERY_KEYS,
} from '@/api/introducer-dashboard/queries';
import {
  ATTRIBUTION_TYPE,
  type CreateManualAttributionBody,
  MANUAL_ATTRIBUTION_OUTCOME,
  type ManualAttributionOutcome,
  type ManualAttributionResult,
} from '@/api/introducer-dashboard/types';
import { _postManualAttribution } from '@/api/referral/mutation';
import { REFERRAL_QUERY_KEYS } from '@/api/referral/queries';
import WarningIcon from '@/assets/svg/ui-elements/alert-triangle-yellow.svg';
import CheckCircleIcon from '@/assets/svg/ui-elements/check-circle.svg';
import { Badge, Button, theme, Tooltip, Typography } from '@/shared/design-system';
import { DeniedReasonTrigger, Permission, useCan } from '@/shared/modules/permissions';
import { getInitials } from '@/utils/get-initials';

import { StepProgress } from './step-progress';
import {
  AffectedEntitiesHeader,
  AffectedEntitiesPanel,
  AffectedEntityRow,
  CardContainer,
  FooterButtons,
  InfoBanner,
  StepContainer,
  SummaryRow,
} from './styled';
import type { ManualAttributionState } from './types';

interface StepReviewProps {
  state: ManualAttributionState;
  onBack: () => void;
  onClose: () => void;
}

const SUCCESS_OUTCOMES: ManualAttributionOutcome[] = [
  MANUAL_ATTRIBUTION_OUTCOME.INVITATION_SENT,
  MANUAL_ATTRIBUTION_OUTCOME.ATTRIBUTED_AS_NEW_INVESTOR,
  MANUAL_ATTRIBUTION_OUTCOME.FUND_INTRODUCTION_ACTIVATED,
];

const BLOCKED_OUTCOMES: ManualAttributionOutcome[] = [
  MANUAL_ATTRIBUTION_OUTCOME.ALREADY_HAS_INTRODUCER,
  MANUAL_ATTRIBUTION_OUTCOME.ALREADY_INVITED,
  MANUAL_ATTRIBUTION_OUTCOME.ALREADY_ACTIVE,
];

const WARNING_OUTCOMES: ManualAttributionOutcome[] = [
  MANUAL_ATTRIBUTION_OUTCOME.NOT_ELIGIBLE_BONUS_PERIOD,
  MANUAL_ATTRIBUTION_OUTCOME.NOT_ELIGIBLE_ACTIVE_INTRODUCTION,
];

const SUCCESS_TITLES: Partial<Record<ManualAttributionOutcome, string>> = {
  [MANUAL_ATTRIBUTION_OUTCOME.INVITATION_SENT]: 'Invitation sent successfully',
  [MANUAL_ATTRIBUTION_OUTCOME.ATTRIBUTED_AS_NEW_INVESTOR]: 'New Investor successfully assigned',
};

export const StepReview = ({ state, onBack, onClose }: StepReviewProps) => {
  const queryClient = useQueryClient();
  const canManageReferrals = useCan(Permission.ReferralAdminManage);
  const [result, setResult] = useState<ManualAttributionResult | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const { mutate, isPending } = useMutation({
    mutationFn: _postManualAttribution,
    onSuccess: (data) => {
      setResult(data);
      setApiError(null);
      queryClient.invalidateQueries({
        queryKey: [INTRODUCER_DASHBOARD_QUERY_KEYS.adminIntroducers],
      });
      queryClient.invalidateQueries({
        queryKey: [INTRODUCER_DASHBOARD_QUERY_KEYS.adminFundActivities],
      });
      queryClient.invalidateQueries({
        queryKey: [INTRODUCER_DASHBOARD_QUERY_KEYS.adminSummary],
      });
      queryClient.invalidateQueries({
        queryKey: [INTRODUCER_DASHBOARD_QUERY_KEYS.investors],
      });
      queryClient.invalidateQueries({
        queryKey: [REFERRAL_QUERY_KEYS.pendingIntroducerInvitations],
      });
    },
    onError: (error: Error) => {
      setApiError(error.message || 'An error occurred. Please try again.');
    },
  });

  const handleExecute = () => {
    if (!canManageReferrals || !state.introducer || !state.user) return;

    const body: CreateManualAttributionBody = {
      introducerEmail: state.introducer.email,
      investorEmail: state.user.email,
      attributionType:
        state.attributionType === 'Fund' ? ATTRIBUTION_TYPE.FUND : ATTRIBUTION_TYPE.GENERAL,
      ...(state.attributionType === 'Fund' && state.fund ? { fundId: state.fund.id } : {}),
      ...(state.user.fromAddressBook && state.user.name ? { inviteeName: state.user.name } : {}),
    };

    mutate(body);
  };

  const isSuccessOutcome = result && SUCCESS_OUTCOMES.includes(result.outcome);
  const isBlockedOutcome = result && BLOCKED_OUTCOMES.includes(result.outcome);
  const isWarningOutcome = result && WARNING_OUTCOMES.includes(result.outcome);

  // On an A2/B2 attribution, the investor's owned legal entities follow them to
  // the same introducer. The list (name + logo) is resolved by the BFF from the
  // ownership/shareholder signal, not the mutation response. Prefetch as soon as
  // a user is selected so the data is cached before the success screen renders —
  // this avoids the panel popping in a beat after the success card.
  const investorUserId = state.user?.id ?? undefined;
  const { data: ownedEntitiesData } = useQuery({
    ..._getInvestorOwnedEntities(investorUserId),
    enabled: !!investorUserId,
  });

  // Success screen
  if (isSuccessOutcome && result) {
    // Owned entities only follow an A2/B2 attribution — never the other success
    // outcomes (invitation sent, fund introduction activated).
    const ownedEntities =
      result.outcome === MANUAL_ATTRIBUTION_OUTCOME.ATTRIBUTED_AS_NEW_INVESTOR
        ? (ownedEntitiesData?.entities ?? [])
        : [];

    return (
      <StepContainer>
        <Stack
          flexDirection="column"
          alignItems="center"
          textAlign="center"
          paddingTop="40px"
          gap="16px"
        >
          <Stack
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            height="40px"
            width="40px"
            bgcolor={theme.colors.brand.solid}
            borderRadius="8px"
          >
            <CheckCircleIcon color="white" />
          </Stack>
          <Typography variant="body1" fontWeight={600}>
            {SUCCESS_TITLES[result.outcome] ?? 'Attribution successful'}
          </Typography>
          <Typography variant="body3" color={alpha(theme.colors.text.primary, 0.6)}>
            {result.message}
          </Typography>
        </Stack>
        {ownedEntities.length > 0 && (
          <AffectedEntitiesPanel>
            <AffectedEntitiesHeader>
              <Typography variant="body3" color={alpha(theme.colors.text.primary, 0.6)}>
                The following Legal Entities owned by{' '}
                <Typography as="span" variant="body3" fontWeight={600}>
                  {state.user?.name ?? state.user?.email}
                </Typography>{' '}
                have also been automatically attributed as New Investor of{' '}
                <Typography as="span" variant="body3" fontWeight={600}>
                  {state.introducer?.name}
                </Typography>
                :
              </Typography>
            </AffectedEntitiesHeader>
            {ownedEntities.map((entity) => (
              <AffectedEntityRow key={entity.id}>
                <Avatar
                  src={entity.logoUrl ?? undefined}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    bgcolor: alpha(theme.colors.text.primary, 0.05),
                    fontSize: '14px',
                  }}
                >
                  <Typography variant="body3" color={alpha(theme.colors.text.primary, 0.6)}>
                    {getInitials(entity.name, true)}
                  </Typography>
                </Avatar>
                <Typography variant="body3">{entity.name}</Typography>
              </AffectedEntityRow>
            ))}
          </AffectedEntitiesPanel>
        )}
        <FooterButtons>
          <Button color="tertiary" variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button color="primary" variant="solid" onClick={onClose}>
            Done
          </Button>
        </FooterButtons>
      </StepContainer>
    );
  }

  return (
    <StepContainer>
      <StepProgress currentStep={4} />

      {isBlockedOutcome && result && (
        <InfoBanner severity="error">
          <Typography variant="body4" color="#F0392C">
            {result.message}
          </Typography>
        </InfoBanner>
      )}

      {isWarningOutcome && result && (
        <>
          <Stack alignItems="flex-start">
            <WarningIcon style={{ width: 40, height: 40 }} />
          </Stack>
          <Typography variant="body1" fontWeight={600}>
            Attribution recorded (not eligible)
          </Typography>
          <Typography variant="body3" color="#818593">
            {result.message}
          </Typography>
        </>
      )}

      {apiError && (
        <InfoBanner severity="error">
          <Typography variant="body4" color="#F0392C">
            {apiError}
          </Typography>
        </InfoBanner>
      )}

      <CardContainer>
        <Typography variant="body2" fontWeight={600}>
          Attribution Summary
        </Typography>
        <SummaryRow>
          <Typography variant="body3" color={alpha(theme.colors.text.primary, 0.8)}>
            Introducer
          </Typography>
          <Stack flexDirection="row" alignItems="center" gap="8px">
            <Typography variant="body3">{state.introducer?.name}</Typography>
            <Badge label="Verified" color="primary" variant="subtle" />
          </Stack>
        </SummaryRow>
        <SummaryRow>
          <Typography variant="body3" color={alpha(theme.colors.text.primary, 0.8)}>
            User
          </Typography>
          <Stack flexDirection="row" alignItems="center" gap="8px">
            <Typography variant="body3">{state.user?.name ?? state.user?.email}</Typography>
            <Badge
              label={state.user?.isNew ? 'New' : 'Existing User'}
              color="primary"
              variant="subtle"
            />
          </Stack>
        </SummaryRow>
        {isBlockedOutcome &&
          result?.outcome === MANUAL_ATTRIBUTION_OUTCOME.ALREADY_HAS_INTRODUCER && (
            <SummaryRow>
              <Typography variant="body3" color={alpha(theme.colors.text.primary, 0.8)}>
                Current Introducer
              </Typography>
              <Typography variant="body3" color={theme.colors.text.info}>
                {state.user?.currentIntroducer ?? '-'}
              </Typography>
            </SummaryRow>
          )}
        <SummaryRow>
          <Typography variant="body3" color={alpha(theme.colors.text.primary, 0.8)}>
            Attribution Type
          </Typography>
          <Typography variant="body3" color={theme.colors.text.info}>
            {state.attributionType === 'Fund' ? 'Fund Introduction' : 'General Referral'}
          </Typography>
        </SummaryRow>
        {state.attributionType === 'Fund' && state.fund && (
          <SummaryRow>
            <Typography variant="body3" color={alpha(theme.colors.text.primary, 0.8)}>
              Fund
            </Typography>
            <Typography variant="body3">{state.fund.name}</Typography>
          </SummaryRow>
        )}
      </CardContainer>

      <FooterButtons>
        <Button color="tertiary" variant="outline" onClick={result ? onClose : onBack}>
          {result ? 'Close' : 'Back'}
        </Button>
        {!result ? (
          <Tooltip
            title={
              canManageReferrals
                ? ''
                : 'You do not have permission to attribute a referral manually'
            }
          >
            <DeniedReasonTrigger denied={!canManageReferrals}>
              <Button
                color="primary"
                variant="solid"
                disabled={isPending || !canManageReferrals}
                onClick={handleExecute}
              >
                {isPending ? 'Executing...' : 'Next'}
              </Button>
            </DeniedReasonTrigger>
          </Tooltip>
        ) : (
          <Button color="primary" variant="solid" onClick={onClose}>
            Done
          </Button>
        )}
      </FooterButtons>
    </StepContainer>
  );
};
